import { ttsUrl, API } from "@/lib/api";

// One shared audio element so narration never overlaps itself.
let audioEl = null;
let draftMode = false;
let seqToken = 0; // bumping this cancels any in-flight sequence
let defaultVoice = "nova"; // English kid-friendly default
// Learner-facing playback speed. Applied as audio.playbackRate on every
// utterance so a slowdown/speed-up takes effect immediately, no re-caching.
let playbackSpeed = 1;
const SPEED_KEY = "zl_playback_speed";
if (typeof window !== "undefined") {
  const stored = parseFloat(window.localStorage?.getItem(SPEED_KEY) || "");
  if (stored && stored >= 0.5 && stored <= 2) playbackSpeed = stored;
}

export function setDraftMode(on) {
  draftMode = !!on;
}

// Language-aware voice override (used by Foreign Language lessons). Pass a
// voice from the lesson payload; passing null/undefined restores English.
export function setDefaultVoice(voice) {
  defaultVoice = voice && typeof voice === "string" ? voice : "nova";
}

export function getDefaultVoice() {
  return defaultVoice;
}

// Learner playback speed (audio.playbackRate). Clamped to [0.5, 2].
export function setPlaybackSpeed(rate) {
  const s = Math.min(Math.max(parseFloat(rate) || 1, 0.5), 2);
  playbackSpeed = s;
  try { window.localStorage?.setItem(SPEED_KEY, String(s)); } catch (_) {}
  if (audioEl) audioEl.playbackRate = s;
  return s;
}

export function getPlaybackSpeed() {
  return playbackSpeed;
}

function ensureAudio() {
  if (!audioEl) audioEl = new Audio();
  audioEl.pause();
  audioEl.onended = null;
  audioEl.playbackRate = playbackSpeed;
  return audioEl;
}

async function speakDraft(text, voice, speed, onEnd) {
  try {
    const token = localStorage.getItem("cla_token");
    const res = await fetch(`${API}/tts-draft`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ text, voice, speed }),
    });
    if (!res.ok) { onEnd && onEnd(); return; }
    const blob = await res.blob();
    const el = ensureAudio();
    el.src = URL.createObjectURL(blob);
    el.currentTime = 0;
    if (onEnd) el.onended = onEnd;
    const p = el.play();
    if (p && p.catch) p.catch(() => onEnd && onEnd());
  } catch (_) { onEnd && onEnd(); }
}

// speak(text, voice, { speed, onEnd }) — omit `voice` (or pass null) to use the
// module default voice, which Foreign Language lessons flip via setDefaultVoice.
export function speak(text, voice, opts = {}) {
  if (!text) { opts.onEnd && opts.onEnd(); return; }
  const speed = opts.speed || 1;
  const v = voice || defaultVoice;
  if (draftMode) { speakDraft(text, v, speed, opts.onEnd); return; }
  try {
    const el = ensureAudio();
    el.src = ttsUrl(text, v, speed);
    el.currentTime = 0;
    if (opts.onEnd) el.onended = opts.onEnd;
    const p = el.play();
    if (p && p.catch) p.catch(() => opts.onEnd && opts.onEnd());
  } catch (_) { opts.onEnd && opts.onEnd(); }
}

// Play a list of items one after another (used by autoplay to read a question
// then each answer choice while highlighting it). Returns a cancel function.
// items: [{ text, before?(i), after?(i) }]
export function speakSequence(items, opts = {}) {
  const my = ++seqToken;
  const list = (items || []).filter(Boolean);
  let i = 0;
  const step = () => {
    if (my !== seqToken) return;
    if (i >= list.length) { opts.onDone && opts.onDone(); return; }
    const it = list[i];
    it.before && it.before(i);
    speak(it.text, opts.voice, {
      speed: opts.speed || 1,
      onEnd: () => {
        if (my !== seqToken) return;
        it.after && it.after(i);
        i += 1;
        setTimeout(step, opts.gap != null ? opts.gap : 350);
      },
    });
  };
  step();
  return () => { if (my === seqToken) seqToken += 1; };
}

export function stopSpeaking() {
  if (audioEl) { audioEl.onended = null; audioEl.pause(); }
}

// Cancel any narration AND any running sequence.
export function cancelSpeech() {
  seqToken += 1;
  stopSpeaking();
}

// Step-aware audio playback for the tutorial player. Priority:
//   1. step.audio_note_id  → play the parent/teacher's recorded voice-note
//   2. step.voice_id set   → generate via /api/tts-preview using the chosen
//                            provider + voice (supports Gemini too)
//   3. fall back to speak(text)
export async function playStepAudio(step, text, opts = {}) {
  const speed = opts.speed || 1;
  const onEnd = opts.onEnd;
  const token = typeof localStorage !== "undefined" ? localStorage.getItem("cla_token") : null;

  const playBlob = async (url) => {
    const el = ensureAudio();
    el.src = url;
    el.currentTime = 0;
    if (onEnd) el.onended = onEnd;
    try { const p = el.play(); if (p && p.catch) p.catch(() => onEnd && onEnd()); }
    catch (_) { onEnd && onEnd(); }
  };

  if (step && step.audio_note_id && token) {
    try {
      const res = await fetch(`${API}/voice-notes/${step.audio_note_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) { await playBlob(URL.createObjectURL(await res.blob())); return; }
    } catch (_) { /* fall through */ }
  }

  if (step && step.voice_id && token) {
    try {
      const res = await fetch(`${API}/tts-preview`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          provider: step.voice_provider || "openai",
          voice: step.voice_id,
          text: text || "",
          speed,
        }),
      });
      if (res.ok) { await playBlob(URL.createObjectURL(await res.blob())); return; }
    } catch (_) { /* fall through */ }
  }

  // Fallback: browser-cached /api/tts URL
  speak(text, null, { speed, onEnd });
}
